/**
 * Live Solar PPA / lease pricing for the editor viewport.
 * Rates come from the same Sandbox Pricing Calculator workbook the V3 pre-contract
 * step uses; PPW / EPC come from the Excel slope-intercept tables at the design's
 * production multiplier. One request drives both the canvas toolbar button and the
 * recommendation card, so the fetch lives in this store instead of either component.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import designServices from '../../../../services/oneButtonServices/designServices';
import { hydrateOpportunityStore } from '../../../../pre-contract/lib/preContractTitle';
import {
    fetchPreContractSandboxPpaRates,
    sandboxEscalatorPercent,
    type PreContractSandboxPpaOffer,
} from '../../../../pre-contract/lib/preContractSandboxPricing';
import {
    quotePartnerFromTables,
    type PpwPartnerId,
} from '../../../../pre-contract/lib/preContractPpwPricing';
import { resolveUtilityCompanyCode } from '../oneBUTTONDashboard/utils/finance/pricing';
import { resolveEnergyCommunityLikelyQualifies } from './EnergyCommunityNotice';

export type EditorPricingOption = {
    id: string;
    partner: string;
    financeMethod: string;
    term: number | null;
    escalatorPercent: number | null;
    minRate: number | null;
    rateCap: number | null;
    ppw: number | null;
    epc: number | null;
    /** Why PPW / EPC are unavailable for this partner, when they are. */
    ppwReason: string | null;
};

export type EditorPricingStatus = 'idle' | 'loading' | 'ready' | 'error';

type EditorPricingState = {
    /** True while the viewer is mounted and pricing is being kept current. */
    active: boolean;
    status: EditorPricingStatus;
    options: EditorPricingOption[];
    recommendation: EditorPricingOption | null;
    /** Why pricing cannot be requested yet (missing design or opportunity inputs). */
    blockReason: string | null;
    errorMessage: string | null;
    /** Production multiplier used for the last PPW / EPC quote. */
    usedMultiplier: number | null;
    /** System size in kW DC used for the last PPW / EPC quote. */
    usedSystemKw: number | null;
    /** Annual production in kWh used for the last PPW / EPC quote. */
    usedAnnualProductionKwh: number | null;
    cardOpen: boolean;
    setCardOpen: (open: boolean) => void;
    toggleCard: () => void;
};

export const useEditorPricingStore = create<EditorPricingState>()((set) => ({
    active: false,
    status: 'idle',
    options: [],
    recommendation: null,
    blockReason: null,
    errorMessage: null,
    usedMultiplier: null,
    usedSystemKw: null,
    usedAnnualProductionKwh: null,
    cardOpen: false,
    setCardOpen: (open) => set({ cardOpen: open }),
    toggleCard: () => set((state) => ({ cardOpen: !state.cardOpen })),
}));

const PREFERRED_ESCALATORS = [2.99, 2.9] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function nearlyEqual(a: number, b: number): boolean {
    return Math.abs(a - b) <= 0.011;
}

export function isEnfinPartner(partner: string): boolean {
    const normalized = partner.toLowerCase().replace(/[^a-z0-9]/g, '');
    return normalized.includes('enfin') || normalized.includes('energyfinance');
}

/** Only Enfin and LightReach have PPW slope/intercept tables. */
function ppwTablePartner(partner: string): PpwPartnerId | null {
    const normalized = partner.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (isEnfinPartner(partner)) return 'Enfin';
    if (normalized.includes('lightreach')) return 'LightReach';
    return null;
}

/** LightReach / Enfin sheets split NYC out of NY. */
function ppwTableState(state: string, city: string): string {
    const code = state.trim().toUpperCase();
    if (code !== 'NY') return code;
    const normalizedCity = city.trim().toLowerCase();
    const isNyc =
        normalizedCity.includes('new york') ||
        normalizedCity === 'nyc' ||
        normalizedCity.includes('brooklyn') ||
        normalizedCity.includes('queens');
    return isNyc ? 'NYC' : code;
}

/** Pair each workbook rate row with PPW / EPC at the current design multiplier. */
export function buildEditorPricingOptions(input: {
    offers: PreContractSandboxPpaOffer[];
    state: string;
    city: string;
    multiplier: number;
    systemWatts: number;
    annualProductionKwh: number;
}): EditorPricingOption[] {
    const { offers, state, city, multiplier, systemWatts, annualProductionKwh } = input;
    const tableState = ppwTableState(state, city);

    return offers.map((offer) => {
        const escalatorPercent = sandboxEscalatorPercent(offer.escalator);
        const partnerId = ppwTablePartner(offer.partner);
        const quote = partnerId
            ? quotePartnerFromTables({
                  partner: partnerId,
                  state: tableState,
                  rate: offer.startingRate,
                  escalator: escalatorPercent / 100,
                  multiplier,
                  systemWatts,
                  annualProductionKwh,
              })
            : null;

        return {
            id: offer.id,
            partner: offer.partner,
            financeMethod: offer.financeMethod || offer.offerType || '—',
            term: offer.term,
            escalatorPercent,
            minRate: offer.startingRate,
            rateCap: offer.rateCap,
            ppw: quote?.available ? quote.ppw : null,
            epc: quote?.available ? quote.epc : null,
            ppwReason: partnerId
                ? quote?.available
                    ? null
                    : (quote?.reason ?? 'PPW unavailable')
                : `No PPW table for ${offer.partner}`,
        };
    });
}

function preferredEscalatorPool(options: EditorPricingOption[]): EditorPricingOption[] {
    for (const escalator of PREFERRED_ESCALATORS) {
        const matching = options.filter(
            (option) =>
                option.escalatorPercent != null && nearlyEqual(option.escalatorPercent, escalator),
        );
        if (matching.length) return matching;
    }
    return options;
}

export function pickEditorPricingRecommendation(
    options: EditorPricingOption[],
    preferEnfin: boolean,
): EditorPricingOption | null {
    if (!options.length) return null;

    const lowestEpc = (pool: EditorPricingOption[]): EditorPricingOption =>
        pool.reduce((best, option) => ((option.epc ?? Infinity) < (best.epc ?? Infinity) ? option : best));
    const lowestRate = (pool: EditorPricingOption[]): EditorPricingOption =>
        pool.reduce((best, option) =>
            (option.minRate ?? Infinity) < (best.minRate ?? Infinity) ? option : best,
        );

    const priced = options.filter((option) => option.epc != null && Number.isFinite(option.epc));

    if (preferEnfin) {
        const enfinPriced = priced.filter((option) => isEnfinPartner(option.partner));
        if (enfinPriced.length) return lowestEpc(preferredEscalatorPool(enfinPriced));
        const enfinAny = options.filter((option) => isEnfinPartner(option.partner));
        if (enfinAny.length) return lowestRate(preferredEscalatorPool(enfinAny));
    }

    if (priced.length) return lowestEpc(preferredEscalatorPool(priced));
    return lowestRate(preferredEscalatorPool(options));
}

export function formatEditorMoney(value: number | null): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
}

export function formatEditorPpw(value: number | null): string {
    return value != null && Number.isFinite(value) ? `$${value.toFixed(2)}/W` : '—';
}

export function formatEditorRate(value: number | null): string {
    return value != null && Number.isFinite(value) ? `$${value.toFixed(3)}` : '—';
}

export function formatEditorEscalator(value: number | null): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `${Math.round(value * 100) / 100}%`;
}

export function formatEditorMultiplier(value: number | null): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return value.toFixed(3);
}

export function formatEditorSystemKw(value: number | null): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return `${value.toFixed(3)} kW`;
}

export function formatEditorProduction(value: number | null): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return `${Math.round(value).toLocaleString()} kWh`;
}

function sessionValue(key: string): string {
    try {
        return String(sessionStorage.getItem(key) ?? '').trim();
    } catch {
        return '';
    }
}

/** Runs the workbook request for the current design. Mount once, from the viewer. */
export function useEditorPricingSync(enabled: boolean): void {
    const totalSystemSize = useViewerStore((state) => state.totalSystemSize);
    const actualWattage = useViewerStore((state) => state.actualWattage);
    const storeMultiplier = useViewerStore((state) => state.multiplier);
    const refreshingShade = useViewerStore((state) => state.refreshingShade);
    const selectedLossesVendor = useViewerStore((state) => state.selectedLossesVendor);
    const solarUtilityData = useOneButtonStore((state) => state.solarUtilityData);
    const setSolarUtilityData = useOneButtonStore((state) => state.setSolarUtilityData);
    const utilityCompanyName = useOneButtonStore((state) => state.utilityCompanyName);
    const storeOpportunityId = useOneButtonStore((state) => state.opportunityID);
    const storeSalesForceId = useOneButtonStore((state) => state.salesForceId);
    const opportunityDetails = useOneButtonStore((state) => state.opportunityDetails);
    const energyCommunityStatus = useOneButtonStore((state) => state.energyCommunityStatus);

    const salesForceId = storeSalesForceId?.trim() || sessionValue('SalesForceId');
    const opportunityId = String(storeOpportunityId ?? '').trim();

    /** Canvas-first loads skip the dashboard orchestrator, so the ids arrive here unset. */
    useEffect(() => {
        if (!enabled || opportunityId || !salesForceId) return;
        void hydrateOpportunityStore(salesForceId);
    }, [enabled, opportunityId, salesForceId]);

    useEffect(() => {
        if (!enabled || !opportunityId || solarUtilityData) return undefined;
        let cancelled = false;
        void designServices
            .RetrieveUtilityInformation(opportunityId)
            .then((result) => {
                if (cancelled) return;
                setSolarUtilityData(result?.utilityDetails ?? null);
            })
            .catch(() => {
                /* utility details stay unset; the card reports the missing input */
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, opportunityId, setSolarUtilityData, solarUtilityData]);

    const utility = asRecord(asRecord(solarUtilityData)?.SolarUtility) ?? {};
    const installationAddress = asRecord(asRecord(opportunityDetails)?.InstallationAddress);
    const state =
        String(
            utility.StateId ??
                installationAddress?.stateCode ??
                installationAddress?.State ??
                installationAddress?.StateId ??
                '',
        )
            .trim()
            .toUpperCase() || sessionValue('stateCode').toUpperCase();
    const city = String(installationAddress?.city ?? installationAddress?.City ?? '').trim();
    const utilityCompany =
        resolveUtilityCompanyCode(utility, utilityCompanyName) || sessionValue('Utility_Company');
    const systemKw = Number(totalSystemSize);
    const annualProduction = Number(actualWattage);
    const systemWatts = systemKw > 0 ? Math.round(systemKw * 1000) : 0;
    /** Shade multiplier drives the PPW curve; kWh/kW ÷ 1000 is the fallback. */
    const multiplier =
        Number.isFinite(storeMultiplier) && storeMultiplier > 0
            ? storeMultiplier
            : systemKw > 0 && annualProduction > 0
              ? Math.round((annualProduction / systemKw / 1000) * 1000) / 1000
              : 0;

    const preferEnfin =
        isEnfinPartner(String(selectedLossesVendor ?? '')) ||
        resolveEnergyCommunityLikelyQualifies(opportunityDetails, energyCommunityStatus);

    let blockReason: string | null = null;
    if (!Number.isFinite(systemKw) || systemKw <= 0) {
        blockReason = 'Add modules to price this design.';
    } else if (refreshingShade) {
        blockReason = 'Waiting for shade calculation…';
    } else if (!Number.isFinite(annualProduction) || annualProduction <= 0) {
        blockReason = 'Waiting for production values…';
    } else if (!state) {
        blockReason = 'Install state is not available yet.';
    } else if (!utilityCompany) {
        blockReason = 'Utility company is not set on this opportunity.';
    }

    useEffect(() => {
        if (!enabled) return undefined;

        if (blockReason) {
            useEditorPricingStore.setState({
                status: 'idle',
                options: [],
                recommendation: null,
                blockReason,
                errorMessage: null,
                usedMultiplier: multiplier > 0 ? multiplier : null,
                usedSystemKw: systemKw > 0 ? systemKw : null,
                usedAnnualProductionKwh: annualProduction > 0 ? annualProduction : null,
            });
            return undefined;
        }

        let cancelled = false;
        const timer = window.setTimeout(() => {
            useEditorPricingStore.setState({
                status: 'loading',
                blockReason: null,
                errorMessage: null,
            });
            void fetchPreContractSandboxPpaRates({
                state,
                utility: utilityCompany,
                batteryCount: 0,
            })
                .then((rates) => {
                    if (cancelled) return;
                    const options = buildEditorPricingOptions({
                        offers: rates?.offers ?? [],
                        state,
                        city,
                        multiplier,
                        systemWatts,
                        annualProductionKwh: annualProduction,
                    });
                    useEditorPricingStore.setState(
                        options.length
                            ? {
                                  status: 'ready',
                                  options,
                                  recommendation: pickEditorPricingRecommendation(
                                      options,
                                      preferEnfin,
                                  ),
                                  errorMessage: null,
                                  usedMultiplier: multiplier,
                                  usedSystemKw: systemKw,
                                  usedAnnualProductionKwh: annualProduction,
                              }
                            : {
                                  status: 'error',
                                  options: [],
                                  recommendation: null,
                                  errorMessage:
                                      'The workbook returned no Solar PPA or lease rates.',
                                  usedMultiplier: multiplier,
                                  usedSystemKw: systemKw,
                                  usedAnnualProductionKwh: annualProduction,
                              },
                    );
                })
                .catch((error: unknown) => {
                    if (cancelled) return;
                    console.error('Editor Sandbox Pricing request failed', error);
                    useEditorPricingStore.setState({
                        status: 'error',
                        options: [],
                        recommendation: null,
                        errorMessage:
                            error instanceof Error
                                ? error.message
                                : 'Live pricing could not be loaded.',
                        usedMultiplier: multiplier,
                        usedSystemKw: systemKw,
                        usedAnnualProductionKwh: annualProduction,
                    });
                });
        }, 500);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [
        annualProduction,
        blockReason,
        city,
        enabled,
        multiplier,
        preferEnfin,
        state,
        systemKw,
        systemWatts,
        utilityCompany,
    ]);

    useEffect(() => {
        useEditorPricingStore.setState({ active: enabled });
        if (enabled) return undefined;
        useEditorPricingStore.setState({
            status: 'idle',
            options: [],
            recommendation: null,
            blockReason: null,
            errorMessage: null,
            usedMultiplier: null,
            usedSystemKw: null,
            usedAnnualProductionKwh: null,
            cardOpen: false,
        });
        return undefined;
    }, [enabled]);
}
